import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { HttpService } from '../../services/http.service';
import { CustomToastComponent } from '../toast/custom-toast.component';

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: {
        status: string; // "APPROVED", "REJECTED", or "ALREADY_APPROVED"
    };
}

@Component({
    selector: 'app-parent-approval',
    templateUrl: './parent-approval.component.html',
    styleUrls: ['./parent-approval.component.scss'],
    imports: [
        ReactiveFormsModule,
        CommonModule,
        FormsModule
    ],
})
export class ParentApprovalComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();
    token: string | null = null;
    studentData: any = []
    approvalStatus: boolean | null = null;
    isAlreadyApproved: boolean = false;
    isLoading: boolean = false;
    isApproved: any = 0;
window: any;

    constructor(
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private route: ActivatedRoute
    ) { }

    ngOnInit() {
        this.getTokenFromUrl();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getTokenFromUrl(): void {
        this.route.queryParams.subscribe(params => {
            this.token = params['token'] || null;

            if (!this.token) {
                this.showErrorToast('Token not found in URL');
            } else {
                this.checkCurrentStatus();
                this.getStudentDetails();
            }
        });
    }

    private checkCurrentStatus(): void {
        if (!this.token) return;

        this.isLoading = true;
        const endpoint = `students/parent-approval/status?token=${this.token}`;

        // Assuming you have an endpoint to check current status
        this.httpService.get<ApiDataResponse>(endpoint)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => this.handleStatusResponse(response),
                error: () => {
                    this.isLoading = false;
                    // If status check fails, assume no decision made yet
                    this.approvalStatus = null;
                    this.isAlreadyApproved = false;
                }
            });
    }

    private handleStatusResponse(response: ApiDataResponse): void {
        this.isLoading = false;
        const status = response.data.status.toUpperCase();
        if (status === 'ALREADY_APPROVED') {
            this.isAlreadyApproved = true;
            this.approvalStatus = true;
        } else if (status === 'APPROVED') {
            this.isAlreadyApproved = false;
            this.approvalStatus = true;
        } else if (status === 'REJECTED') {
            this.isAlreadyApproved = false;
            this.approvalStatus = false;
        } else {
            this.isAlreadyApproved = false;
            this.approvalStatus = null;
        }
    }

    // Methods
    approveRequest() {
        this.updateApprovalStatus(true);
    }

    rejectRequest() {
        this.updateApprovalStatus(false);

    }

    getStatusMessage() {
        if (this.isAlreadyApproved) {
            return 'Already Approved!';
        } else if (this.approvalStatus === true) {
            return 'Approved Successfully!';
        } else if (this.approvalStatus === false) {
            return 'Request Rejected';
        }
        return 'Awaiting Your Decision';
    }

    getStatusIcon() {
        if (this.isAlreadyApproved) {
            return 'fas fa-check-double';
        } else if (this.approvalStatus === true) {
            return 'fas fa-check-circle';
        } else if (this.approvalStatus === false) {
            return 'fas fa-times-circle';
        }
        return 'fas fa-clock';
    }

    getStatusColor() {
        if (this.isAlreadyApproved) {
            return 'status-pending';
        } else if (this.approvalStatus === true) {
            return 'status-approved';
        } else if (this.approvalStatus === false) {
            return 'status-rejected';
        }
        return 'status-pending';
    }

    shouldShowButtons(): boolean {
        return this.approvalStatus === null && !this.isAlreadyApproved && !this.isLoading;
    }

    get isStudent16(): boolean {
        if (!this.studentData?.dateOfBirth) return false;
        const dob = new Date(this.studentData.dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        if (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate())) age--;
        return age >= 16;
    }

    private updateApprovalStatus(isApproved: boolean): void {
        if (!this.token) {
            this.showErrorToast('Token is required');
            return;
        }

        this.isLoading = true;
        const payload = { token: this.token, accountApproved: isApproved };

        this.httpService.post<ApiDataResponse>('students/parent-approval/respond', payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.handleSuccessResponse(response);
                    this.isApproved = isApproved ? 1 : 2;
                },
                error: () => this.handleError()
            });
    }

    private getStudentDetails(): void {
        if (!this.token) {
            this.showErrorToast('Token is required');
            return;
        }

        this.isLoading = true;
        const endpoint = `students/parent-approval/details?token=${this.token}`;

        this.httpService.get<ApiDataResponse>(endpoint)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.studentData = response?.data
                    this.isApproved = this.studentData?.parentApprovalStatus ?? 0;
                },
                error: () => this.handleError()
            });
    }

    private handleSuccessResponse(response: ApiDataResponse): void {
        this.isLoading = false;
        const status = response.data.status.toUpperCase();

        if (status === 'ALREADY_APPROVED') {
            this.isAlreadyApproved = true;
            this.approvalStatus = true;
        } else if (status === 'APPROVED') {
            this.approvalStatus = true;
            this.isAlreadyApproved = false;
        } else if (status === 'REJECTED') {
            this.approvalStatus = false;
            this.isAlreadyApproved = false;
        }

        this.showSuccessToast(response.message);
        // console.log('API Response:', response);
    }

    private handleError(): void {
        this.isLoading = false;
        this.showErrorToast('Something went wrong. Please try again later.');
    }

    private showSuccessToast(message: string): void {
        this.showToast(message, 'success-toast');
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.showToast(message, 'error-toast');
    }

    private showToast(message: string, panelClass: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', panelClass]
        });
    }
}